import { JSXElementConstructor, Key, ReactElement, ReactNode, ReactPortal, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { TrashIcon } from '@radix-ui/react-icons'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useDeleteContract, useFetchContractsByIsAtivo } from '@/lib/hooks/contract'
import CriarContrato from '@/components/form/contrato/criar-contrato-form'
import { FeedbackDialog } from '../feedback-dialog'
import { useCurrentUser } from '@/features/auth/state/auth'
import { PageLayout } from '@/layouts'
import { useFetchIdTenants } from '@/lib/hooks/tenant'
import { useMemo } from 'react'


interface Contract {
  id: number
  nameContrato: string
  id_tenant: string
  typeContrato: string
  createdDate: string
  deletedDate?: string
}

// // Função utilitária para formatar datas no formato desejado
export function formatDate(dateString: string): string {
  if (!dateString) return 'Data inválida' // Retorna um valor padrão se a data for nula ou indefinida

  // Substituir espaço entre data e hora por "T" para que seja reconhecido como formato ISO 8601
  const formattedString = dateString.replace(' ', 'T')

  const date = new Date(formattedString)
  if (isNaN(date.getTime())) return 'Data inválida' // Verifica se a conversão falhou

  return new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

export default function ContratoForm() {
  const [userHasPrivilege, setUserHasPrivilege] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const { data: activeContracts = [], refetch: refetchActiveContracts } = useFetchContractsByIsAtivo(true)
  const { data: deletedContracts = [], refetch: refetchDeletedContracts } = useFetchContractsByIsAtivo(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const user = useCurrentUser()

  const { data: tenants = [], isLoading: loadingTenants, error: tenantError } = useFetchIdTenants()

  const tenantMap = useMemo(() => {
    const map = new Map<string, string>()
    if (Array.isArray(tenants)) {
      tenants.forEach(tenant => {
        const nome = tenant.nome_fantasia || tenant.razao_social || tenant.cnpj
        map.set(tenant.cnpj, nome)
      })
    }
    return map
  }, [tenants])

  const filteredDeletedContracts = deletedContracts
    .filter((contract: { id: null }) => contract.id != null)
    .filter(
      (contract: { nameContrato: string; typeContrato: string }) =>
        contract.nameContrato?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.typeContrato?.toLowerCase().includes(searchTerm.toLowerCase())
    )

  const { mutate: deleteContract } = useDeleteContract()

  const handleDeleteContract = (id: number) => {
    deleteContract(id, {
      onSuccess: () => {
        refetchActiveContracts()
        refetchDeletedContracts()
        setMessage({ type: 'success', text: 'Operação realizada com sucesso!' })
        setIsDialogOpen(true)
      },
      onError: error => {
        console.error('Erro ao deletar contrato:', error)
        setMessage({ type: 'error', text: `Erro ao deletar contrato:${error}` })
        setIsDialogOpen(true)
      }
    })
  }

  return (
    <PageLayout
      title="Gerenciamento de Contratos"
      description="Visualize, crie e gerencie seus contratos"
      footer={
        <>
          {user?.typeCargo === 'CEO' && (
            <Button
              onClick={() => setUserHasPrivilege(!userHasPrivilege)}
              className="bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              {userHasPrivilege ? 'Remover Privilégios' : 'Conceder Privilégios'}
            </Button>
          )}
        </>
      }
    >
      {loadingTenants ? (
        <p>Carregando Tenant...</p>
      ) : tenantError ? (
        <p>Erro ao carregar Tenant.</p>
      ) : (
        <Tabs defaultValue="list">
          <TabsList className="grid w-full grid-cols-3 bg-background hover-background-secondary">
            <TabsTrigger value="list">Lista de Contratos</TabsTrigger>
            <TabsTrigger value="create">Criar Novo Contrato</TabsTrigger>
            <TabsTrigger value="deleted" disabled={!userHasPrivilege}>
              Contratos Excluídos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="list">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome do Contrato</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Data de Criação</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeContracts.map((contract: Contract) => (
                  <TableRow key={contract.id}>
                    <TableCell>{contract.nameContrato}</TableCell>
                    <TableCell>{tenantMap.get(contract.id_tenant) ?? contract.id_tenant}</TableCell>
                    <TableCell>{contract.typeContrato}</TableCell>
                    <TableCell>{formatDate(contract.createdDate)}</TableCell>
                    <TableCell>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteContract(contract.id)}>
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="create">
            <div className="flex justify-center items-center">
              <CriarContrato />
            </div>
          </TabsContent>

          <TabsContent value="deleted">
            {userHasPrivilege ? (
              <>
                <Input
                  placeholder="Buscar contratos excluídos..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="mb-4"
                />
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome do Contrato</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Data de Criação</TableHead>
                      <TableHead>Data de Exclusão</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDeletedContracts.map(
                      (contract: {
                        id: Key | null | undefined
                        nameContrato:
                          | string
                          | number
                          | boolean
                          | ReactElement<any, string | JSXElementConstructor<any>>
                          | Iterable<ReactNode>
                          | ReactPortal
                          | null
                          | undefined
                        typeContrato:
                          | string
                          | number
                          | boolean
                          | ReactElement<any, string | JSXElementConstructor<any>>
                          | Iterable<ReactNode>
                          | ReactPortal
                          | null
                          | undefined
                        createdDate: string
                        deletedAt: string
                      }) => (
                        <TableRow key={contract.id}>
                          <TableCell>{contract.nameContrato}</TableCell>
                          <TableCell>{contract.typeContrato}</TableCell>
                          <TableCell>{formatDate(contract.createdDate)}</TableCell>
                          <TableCell>{contract.deletedAt ? formatDate(contract.deletedAt) : '-'}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </>
            ) : (
              <Alert>
                <AlertTitle>Acesso Restrito</AlertTitle>
                <AlertDescription>Você não tem permissão para acessar esta área.</AlertDescription>
              </Alert>
            )}
          </TabsContent>
        </Tabs>
      )}
      <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />
    </PageLayout>
  )
}
